import deepEqual from "deep-equal";
import { Base64 } from 'js-base64';

import { screenConnect } from "@screen";

import BaseComponent from "@js/components/base_component.jsx";

import BEE from "@js/Bee.js";

import {
  ceScreenEditorSave,
  ceScreenEditorMarkAsDirty,
  ceScreenEditorMarkAsClean,
  ceScreenEditorCancel,
} from "@js/actions/studio/campaign_content_section/screen_editor.js";

import {
  ceSetBeeUID,
  ceFetchBeeTokenFailure,
} from "@js/actions/studio/campaign_content_section/set_bee_uid.js";

import {
  ceOpenSaveCustomRowModal,
  ceSaveCustomRowHtml,
} from "@js/actions/studio/campaign_content_section/custom_rows.js";

class EditorBee extends BaseComponent {
  constructor(props) {
    super(props);
    /**
     * This technically violates our policy against using local state.
     * However, this is a prime candidate for using local state for the following reasons:
     *  - The state is truly local. No other components need to reference this
     *  - Since the state is meaningless to the other Editor components, it does not make sense to pass `dirty: false` to those components
     *  - Having screen state of `dirty: false` in a HugeRTE component that has `this.hugerte.isDirty(): false` could confuse future developers
     *
     * Note: We don't actually use React's state, as this value does not need to be referenced in the render cycle
     */
    this.dirty = false;
  }

  componentDidMount() {
    BEE.getToken((tokenResults, error) => {
      if (
        error ||
        !tokenResults ||
        !tokenResults.token ||
        !tokenResults.token.access_token
      ) {
        this.props.dispatch(ceFetchBeeTokenFailure());
        // We still need to connect the connector to get cancel to work
        this.props.connector.subscribe(this.onConnectorEvent);
      } else {
        this.props.dispatch(ceSetBeeUID(tokenResults.bee_uid));
        this.initializeBEE(tokenResults.token);
      }
    });
  }

  componentWillUnmount() {
    // We need to clear the subscription to the connector
    // Otherwise, a user could open a new editor and click cancel before
    // the subscription is overwritten, causing reference errors.
    let nullFunc = () => {};
    this.props.connector.subscribe(nullFunc);
  }

  render() {
    return (
      <div id="bee-editor-container">
        {this.props.bee_token_error && (
          <p className="alert bee-editor-error">
            {t(`phrases.react_content.error_generating_bee_token`)}
          </p>
        )}
      </div>
    );
  }

  initializeBEE(bee_token) {
    var specialLinks = [
      {
        label: t("phrases.unsubscribe_me"),
        type: t("phrases.unsubscribe"),
        link: "%%unsubscribe_link%%",
      },
      {
        label: t("phrases.unsubscribe"),
        type: t("phrases.unsubscribe"),
        link: "%%unsubscribe_link%%",
      },
      {
        label: t("phrases.view_web_version"),
        type: t("phrases.web_version"),
        link: "%%link_to_web_version%%",
      },
    ];

    specialLinks = specialLinks.concat(
      (this.props.library.forms || []).map((form) => {
        return {
          label: form.name,
          type: "Forms",
          link: form.replacement_code,
        };
      }),
    );

    let mergeTags = this.props.library.available_fields.map((field) => ({
      name: `${field.label} ${
        field.is_custom_field ? t("phrases.react_content.custom_field") : ""
      }`,
      value: `%%${field.code}%%`,
    }));

    let dispatch = this.props.dispatch;
    let self = this;

    var beeConfig = {
      uid: this.props.bee_uid || "bee-uid",
      container: "bee-editor-container",
      autosave: false,
      language: "en-US",
      specialLinks: specialLinks,
      mergeTags: mergeTags,
      onSave: this.onBeeSave,
      onSaveAsTemplate: this.onBeeSaveAsTemplate,
      onSaveRow: this.onBeeSaveRow,
      onAutoSave: this.onBeeAutoSave,
      onSend: this.onBeeSend,
      onError: this.onBeeError,
      trackChanges: true,
      onChange: this.onBeeChange,
      editorFonts: editorFonts,
      saveRows: true,

      contentDialog: {
        saveRow: { handler: this.onBeeContentDialogSaveRow },
        externalContentURLs: window.UserDefinedBeeExternalContentURLs,
      },

      rowsConfiguration: {
        emptyRows: true,
        defaultRows: true,
        externalContentURLs: this.externalContentURLs(),
      },
    };

    BeePlugin.create(bee_token, beeConfig, (beePluginInstance) => {
      // While we still could use React's state here, its an antipattern in Redux (https://redux.js.org/faq/organizing-state#can-i-put-functions-promises-or-other-non-serializable-items-in-my-store-state)
      // So we don't want to get in the habit of keeping non-serializable data in either React State or the Redux store
      this.beeActiveInstance = beePluginInstance;

      if (this.props.bee_json) {
        beePluginInstance.start(JSON.parse(this.props.bee_json));
      } else {
        beePluginInstance.start({});
      }

      this.props.connector.subscribe(this.onConnectorEvent);
    });
  }

  onBeeChange(beeJSON, changeEvent) {
    let hasBeenChanged = changeEvent.description !== "Message opened";
    if (hasBeenChanged && !this.dirty) {
      this.dirty = true;
    }
  }

  onBeeSave(beeJSON, html) {
    this.props.dispatch(ceScreenEditorSave(beeJSON, html, null));
  }

  onBeeSaveAsTemplate(jsonFile) {
    alert("save as template not supported");
  }

  onBeeAutoSave(jsonFile) {
    alert("autosave not supported");
  }

  onBeeSend(htmlFile) {
    alert("onsend not supported");
  }

  onBeeError(errorMessage) {
    console.log(["error loading bee", errorMessage]);
  }

  // BEE calls this callback when the row has been saved and the HTML is available.
  onBeeSaveRow(rowJSON, rowHTML, pageJSON) {
    this.props.dispatch(ceSaveCustomRowHtml(rowJSON, rowHTML));
  }

  // BEE calls this callback when the user requests to save a row. If the save
  // was successful, we call the "resolve" callback with the metadata to add to
  // the row. Otherwise we call "reject" to let BEE know to continue.
  onBeeContentDialogSaveRow(resolve, reject, args) {
    this.onSaveRowResolve = (metadata) => {
      this.onSaveRowResolve = null;
      this.onSaveRowReject = null;

      resolve(metadata);
    };

    this.onSaveRowReject = () => {
      this.onSaveRowResolve = null;
      this.onSaveRowReject = null;

      reject();
    };

    this.props.dispatch(ceOpenSaveCustomRowModal(args));
  }

  onConnectorEvent(e) {
    switch (e.type) {
      case "save":
        if (this.beeActiveInstance) {
          this.beeActiveInstance.save();
        }
        break;
      case "cancel":
        this.props.dispatch(ceScreenEditorCancel(this.dirty));
        break;
      case "save-custom-row-success":
        if ( this.onSaveRowResolve ) {
          this.onSaveRowResolve(e.metadata);
        }
        break;
      case "save-custom-row-aborted":
        if ( this.onSaveRowReject ) {
          this.onSaveRowReject();
        }
        break;
    }
  }

  // Since the BEE code gets loaded over HTTPS (and we cannot control this),
  // Chrome requires that the resources it queries also be loaded over HTTPS.
  // So this feature won't work on servers that don't have HTTPS available.
  enableSavedRows() {
    return window.location.protocol === "https:";
  }

  externalContentURLs() {
    let organizationId = this.props.organization_id;
    let mailingListId = this.props.mailing_list_id;
    let beeSessionOverride = this.props.bee_session_override;

    let externalContentURLs = [];

    let hostPrefix = window.location.origin;

    externalContentURLs.push({
      name: t("phrases.current_mailing_list"),
      value: hostPrefix + Routes.bee_index_api_v2_mailing_list_bee_custom_rows_path(mailingListId, { s: beeSessionOverride }),
    });

    externalContentURLs.push({
      name: t("phrases.all_mailing_lists"),
      value: hostPrefix + Routes.bee_index_api_v2_organization_bee_custom_rows_path(organizationId, { s: beeSessionOverride }),
    });

    this.props.mailing_lists.forEach((list) => {
      const url = hostPrefix + Routes.bee_index_api_v2_mailing_list_bee_custom_rows_path(list.id, { s: beeSessionOverride });

      if ( list.id == mailingListId ) {
        externalContentURLs[0].name  = `${ t("phrases.current_mailing_list") } (${ list.name })`;
        externalContentURLs[0].value = url;
      } else {
        externalContentURLs.push({
          name:  list.name,
          value: url,
        });
      }
    });

    if ( !this.enableSavedRows() ) {
      // If we're connecting over HTTP, then replace the URLs with a data URL
      // that just shows an error message advising the user to use HTTPS.

      for ( let i = 0; i < externalContentURLs.length; ++i ) {
        const httpNotSupportedRows = encodeURIComponent(
          JSON.stringify([{
              "name": t("phrases.react_content.https_required_for_custom_rows.title"),
              "_index": i,
              "columns": [{
                "weight": 12,
                "modules": [{
                  "type": "title",
                  "text": t("phrases.react_content.https_required_for_custom_rows.body"),
                }],
              }],
          }])
        );

        const httpNotSupportedUrl = "data:application/json," + httpNotSupportedRows;

        externalContentURLs[i].value = httpNotSupportedUrl;
      }
    }

    return externalContentURLs;
  }

}

export default screenConnect(EditorBee);

const editorFonts = {
  showDefaultFonts: false,
  customFonts: [
    {
      name: "Arial",
      fontFamily: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
    },
    {
      name: "Courier",
      fontFamily:
        "'Courier New', Courier, 'Lucida Sans Typewriter', 'Lucida Typewriter', monospace",
    },
    {
      name: "Georgia",
      fontFamily: "Georgia, Times, 'Times New Roman', serif",
    },
    {
      name: "Helvetica",
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    },
    {
      name: "Lucida Sans",
      fontFamily:
        "'Lucida Sans Unicode', 'Lucida Grande', 'Lucida Sans', Geneva, Verdana, sans-serif",
    },
    {
      name: "Tahoma",
      fontFamily: "Tahoma, Verdana, Segoe, sans-serif",
    },
    {
      name: "Times New Roman",
      fontFamily:
        "TimesNewRoman, 'Times New Roman', Times, Beskerville, Georgia, serif",
    },
    {
      name: "Trebuchet MS",
      fontFamily:
        "'Trebuchet MS', 'Lucida Grande', 'Lucida Sans Unicode', 'Lucida Sans', Tahoma, sans-serif",
    },
    {
      name: "Verdana",
      fontFamily: "Verdana, Geneva, sans-serif",
    },
    {
      name: "Bitter",
      fontFamily: "'Bitter', Georgia, Times, 'Times New Roman', serif",
      url: "https://fonts.googleapis.com/css?family=Bitter",
    },
    {
      name: "Droid Serif",
      fontFamily: "'Droid Serif', Georgia, Times, 'Times New Roman', serif",
      url: "https://fonts.googleapis.com/css?family=Droid+Serif",
    },
    {
      name: "Lato",
      fontFamily: "'Lato', Tahoma, Verdana, Segoe, sans-serif",
      url: "https://fonts.googleapis.com/css?family=Lato",
    },
    {
      name: "Open Sans",
      fontFamily: "'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif",
      url: "https://fonts.googleapis.com/css?family=Open+Sans",
    },
    {
      name: "Roboto",
      fontFamily: "'Roboto', Tahoma, Verdana, Segoe, sans-serif",
      url: "https://fonts.googleapis.com/css?family=Roboto",
    },
    {
      name: "Source Sans Pro",
      fontFamily: "'Source Sans Pro', Tahoma, Verdana, Segoe, sans-serif",
      url: "https://fonts.googleapis.com/css?family=Source+Sans+Pro",
    },
    {
      name: "Montserrat",
      fontFamily:
        "'Montserrat', 'Trebuchet MS', 'Lucida Grande', 'Lucida Sans Unicode', 'Lucida Sans', Tahoma, sans-serif",
      url: "https://fonts.googleapis.com/css?family=Montserrat",
    },
    {
      name: "Ubuntu",
      fontFamily: "'Ubuntu', Tahoma, Verdana, Segoe, sans-serif",
      url: "https://fonts.googleapis.com/css?family=Ubuntu",
    },
  ],
};
